import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { api, parseErrorDetail } from "../lib/api";

const CENTS = (c) => (c == null ? "—" : `$${(Math.abs(c) / 100).toFixed(2)}`);

export default function OwnerOrderDetail() {
  const nav = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [ord, aud] = await Promise.all([
        api.get(`/admin/orders/${orderId}`),
        api.get(`/admin/orders/${orderId}/audit-history`),
      ]);
      setOrder(ord.data);
      setAudit(aud.data || []);
      // Mark seen fire-and-forget.
      api.post(`/admin/orders/${orderId}/mark-seen`).catch(() => {});
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const onConfirmPrepayment = useCallback(async () => {
    if (!confirmChecked || confirming) return;
    setConfirming(true); setConfirmError("");
    try {
      const r = await api.post(`/admin/orders/${orderId}/confirm-prepayment`, { confirm: true });
      setOrder(r.data);
      setConfirmChecked(false);
      const aud = await api.get(`/admin/orders/${orderId}/audit-history`);
      setAudit(aud.data || []);
    } catch (err) {
      setConfirmError(parseErrorDetail(err));
    } finally {
      setConfirming(false);
    }
  }, [orderId, confirmChecked, confirming]);

  if (loading) return <AdminShell title="Order Detail"><p className="apwo-muted">Loading…</p></AdminShell>;
  if (error || !order) {
    return (
      <AdminShell title="Order Detail">
        <section className="apwo-panel apwo-empty-panel" data-testid="owner-order-detail-error">
          <h3>Unable to load order.</h3>
          <p className="apwo-muted">{error || "Order not found."}</p>
          <div className="apwo-empty-actions">
            <button className="apwo-btn-secondary" onClick={() => nav("/admin/orders")} data-testid="owner-order-detail-back">
              Back to inbox
            </button>
          </div>
        </section>
      </AdminShell>
    );
  }

  const money = order.money || {};

  return (
    <AdminShell title={`Order ${order.order_number}`}>
      <section className="apwo-panel" data-testid="owner-order-detail-panel">
        <div className="apwo-summary-head">
          <h2 data-testid="owner-order-detail-heading">
            Order {order.order_number}
            {order.cancelled_at && <span className="apwo-badge apwo-badge--danger" data-testid="owner-detail-cancelled-badge">Cancelled</span>}
          </h2>
          <button className="apwo-linky" onClick={() => nav("/admin/orders")} data-testid="owner-order-detail-back">← Inbox</button>
        </div>

        {order.cancelled_at && (
          <div className="apwo-cancellation-record" data-testid="owner-cancellation-record">
            <p><strong>Cancellation type:</strong> {order.cancellation_type}</p>
            <p><strong>Cancelled at (Sydney):</strong> {order.cancelled_at_sydney || order.cancelled_at}</p>
            <p><strong>By:</strong> {order.cancelled_by}</p>
            {order.cancellation_reason && <p><strong>Reason:</strong> {order.cancellation_reason}</p>}
            {order.reward_restored_cents > 0 && (
              <p><strong>Reward restored:</strong> {CENTS(order.reward_restored_cents)}</p>
            )}
            {order.replaced_by_order_id && (
              <p>Replaced by <code>{order.replaced_by_order_id}</code>. <button className="apwo-linky" onClick={() => nav(`/admin/orders/${order.replaced_by_order_id}`)}>Open replacement →</button></p>
            )}
          </div>
        )}
        {!order.cancelled_at && order.replaces_order_id && (
          <div className="apwo-cancellation-record" data-testid="owner-replaces-record">
            <p>Replacement for cancelled order <code>{order.replaces_order_id}</code>. <button className="apwo-linky" onClick={() => nav(`/admin/orders/${order.replaces_order_id}`)}>Open original →</button></p>
          </div>
        )}

        <div className="apwo-success-grid">
          <div className="apwo-panel apwo-panel--summary" data-testid="owner-order-detail-lines">
            <h3 className="apwo-summary-section">Items</h3>
            <table className="apwo-summary-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th className="apwo-th-right">Qty</th>
                  <th className="apwo-th-right">Unit (ex GST)</th>
                  <th className="apwo-th-right">Line (ex GST)</th>
                </tr>
              </thead>
              <tbody>
                {(order.lines || []).map((l) => (
                  <tr key={l.product_id} data-testid={`owner-line-${l.product_id}`}>
                    <td>{l.display_name}</td>
                    <td className="apwo-cell-muted">{l.product_id}</td>
                    <td className="apwo-th-right">{l.qty}</td>
                    <td className="apwo-th-right">{CENTS(l.unit_price_cents)}</td>
                    <td className="apwo-th-right">{CENTS(l.line_total_ex_gst_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="apwo-note-block" data-testid="owner-order-detail-note">
              <p className="apwo-ship-title">Customer note</p>
              <p className="apwo-note-view" data-testid="owner-order-note-value">{order.customer_note || <span className="apwo-cell-muted">— none —</span>}</p>
            </div>

            <div className="apwo-audit-history" data-testid="owner-audit-history">
              <h3 className="apwo-summary-section">Audit history</h3>
              {audit.length === 0 && <p className="apwo-muted">No audit events recorded yet.</p>}
              {audit.length > 0 && (
                <ul className="apwo-audit-list" data-testid="owner-audit-list">
                  {audit.map((a, i) => (
                    <li key={i} data-testid={`owner-audit-row-${i}`}>
                      <span className="apwo-audit-when">{a.ts_sydney ? new Date(a.ts_sydney).toLocaleString() : a.ts}</span>
                      <span className="apwo-audit-action">{a.action}</span>
                      <span className="apwo-audit-actor">{a.actor_display_name || a.actor_user_id || "system"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <aside className="apwo-panel apwo-panel--totals" data-testid="owner-order-detail-totals">
            <h3 className="apwo-panel-heading">Money breakdown</h3>
            <div className="apwo-totals-row">
              <span>Product subtotal (ex GST)</span>
              <span data-testid="owner-tot-product-ex">{CENTS(money.product_subtotal_ex_gst_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>Freight (ex GST)</span>
              <span data-testid="owner-tot-freight-ex">{CENTS(money.freight_ex_gst_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>Reward applied</span>
              <span data-testid="owner-tot-reward-applied">{CENTS(money.reward_applied_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>Taxable (ex GST)</span>
              <span data-testid="owner-tot-taxable-ex">{CENTS(money.taxable_ex_gst_cents)}</span>
            </div>
            <div className="apwo-totals-row">
              <span>GST total</span>
              <span data-testid="owner-tot-gst-total">{CENTS(money.gst_total_cents)}</span>
            </div>
            <div className="apwo-totals-row apwo-totals-row--total">
              <span>TOTAL PAYABLE (incl. GST)</span>
              <span data-testid="owner-tot-payable">{CENTS(money.payable_total_cents)}</span>
            </div>

            <div className="apwo-pay-block" data-testid="owner-order-detail-status">
              <p className="apwo-pay-title">Status</p>
              <p className="apwo-cell-muted">
                Payment term: <strong data-testid="owner-detail-payment-term">{order.payment_term || "—"}</strong><br/>
                Order: <strong data-testid="owner-detail-order-status">{order.order_status}</strong><br/>
                Payment: <strong data-testid="owner-detail-payment-status">{order.payment_status}</strong>
                {order.paid_at && (<><br/>Paid at: <strong data-testid="owner-detail-paid-at">{new Date(order.paid_at).toLocaleString()}</strong></>)}
                <br/>Submitted: {new Date(order.created_at).toLocaleString()}
                {Number(order?.money?.reward_applied_cents || 0) > 0 && (
                  <><br/>Reward applied: <strong data-testid="owner-detail-reward-applied">-{CENTS(order.money.reward_applied_cents)}</strong></>
                )}
                {order.earned_reward_cents > 0 && (
                  <><br/>Reward earned: <strong data-testid="owner-detail-earned-reward">{CENTS(order.earned_reward_cents)}</strong></>
                )}
              </p>
              <p className="apwo-fineprint">
                The official Xero tax invoice is sent separately by email once cleared payment is confirmed.
              </p>
            </div>

            {order.payment_term === "PREPAY" && order.payment_status === "UNPAID" && !order.cancelled_at && (
              <div className="apwo-panel apwo-panel--confirm" data-testid="owner-confirm-prepay-block">
                <h3 className="apwo-panel-heading">Confirm Prepayment Received</h3>
                <p className="apwo-fineprint">
                  Estimated Reward on confirmation: {order.earn_eligible
                    ? <strong data-testid="owner-confirm-est-reward">{CENTS(order.earn_preview_cents)}</strong>
                    : <strong data-testid="owner-confirm-est-below">$0 — below $4,000 ex GST threshold</strong>}
                </p>
                <label className="apwo-confirm-check" data-testid="owner-confirm-check-label">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    data-testid="owner-confirm-check"
                  />
                  <span>I confirm that cleared funds for this Prepay order have been received.</span>
                </label>
                {confirmError && (
                  <div className="apwo-alert-error" data-testid="owner-confirm-error">{confirmError}</div>
                )}
                <button
                  className="apwo-btn-primary"
                  disabled={!confirmChecked || confirming}
                  onClick={onConfirmPrepayment}
                  data-testid="owner-confirm-prepay-btn"
                >
                  {confirming ? "Confirming…" : "Confirm Prepayment Received"}
                </button>
              </div>
            )}
          </aside>
        </div>
      </section>
    </AdminShell>
  );
}
